/* ==========================================================================
   FTB - overlay accessibility
   Added 2026-08-24 after the first keyboard and focus audit ever run here.
   Extended the same day to cover the mobile nav drawer as well as the cart.

   NOTE ON THE FILENAME: this started life as ftb-cart-a11y.js and now handles
   every FTB overlay. The name is kept because renaming an asset means editing
   sections/group-overlay.json, which is a ~12KB base64 push, and the loader
   line there is the only reference. Treat this as "overlay a11y".

   WHAT IT FIXES, all measured on the draft theme, not inferred:

   CART DRAWER  <cart-drawer id="cart-drawer">
     1. No role, no aria-modal, no accessible name. Bare tabindex="0" put an
        unlabelled, roleless container into the tab order as its own stop.
     2. No focus trap. Nothing marked the background inert, 57 to 63 focusables
        stayed reachable outside, and focus moved to a background element was
        NOT pulled back.
     3. Focus never returned to the trigger. Closing left activeElement on body.

   MOBILE NAV DRAWER  <header-drawer class="drawer--header">
     Same three faults, and worse on 1: it had no tabindex at all. Plus:
     4. The hamburger's aria-controls="header-menu" pointed at an id that does
        not exist. The real drawer id is dynamic (mobile-menu-sections--...),
        so it is read off the element rather than hard-coded.
     5. The close button was labelled "Show menu" (the cart drawer's correctly
        says "Close Cart"), and its aria-expanded stayed "true" after close.

   TWO THINGS DELIBERATELY LEFT ALONE
     - The nav close button also carries tabindex="-1" and aria-haspopup="true".
       Broadcast focuses it programmatically on open, which works, so the
       tabindex is left as-is rather than fighting the theme.
     - The search popdown and the PhotoSwipe lightbox are NOT covered. Their
       behaviour has not been tested and guessing is what this file exists to
       stop doing.

   IT DOES NOT TOUCH Broadcast's open/close logic. It observes the `is-open`
   class and reacts, so add, remove, quantity and checkout cannot regress
   through it. Verified by canary: qty 4->5->4, $20->$25->$20, count 10->11->10,
   with all FTB enhancements and the new ARIA surviving the AJAX re-render.
   ========================================================================== */
(function () {
  var d = document;

  /* Selector, not id, for the nav drawer: its id is generated per section. */
  var OVERLAYS = [
    { sel: '#cart-drawer', label: 'Your cart' },
    { sel: 'header-drawer', label: 'Menu', nav: true }
  ];

  var lastOutside = null;  /* last thing focused while NO overlay was open */

  function isOpen(el) { return !!el && el.className.indexOf('is-open') > -1; }

  function focusables(el) {
    var sel = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,summary,[tabindex]:not([tabindex="-1"])';
    return [].slice.call(el.querySelectorAll(sel)).filter(function (e) {
      var cs = window.getComputedStyle(e);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      var r = e.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
  }

  function openOverlay() {
    for (var i = 0; i < OVERLAYS.length; i++) {
      var el = d.querySelector(OVERLAYS[i].sel);
      if (isOpen(el)) return el;
    }
    return null;
  }

  /* --- Dialog semantics ---------------------------------------------------
     Re-applied on every state change: these drawers re-render their innards
     over AJAX and can drop an id. aria-modal is toggled, not set once, so a
     closed drawer never claims to be modal. */
  function semantics(el, cfg) {
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', isOpen(el) ? 'true' : 'false');
    /* Cart drawer has .drawer__heading; the nav drawer has no heading at all,
       measured, so it falls back to a literal label. */
    var h = el.querySelector('.drawer__heading');
    if (h) {
      if (!h.id) h.id = 'ftb-overlay-title-' + (cfg.nav ? 'nav' : 'cart');
      el.setAttribute('aria-labelledby', h.id);
      el.removeAttribute('aria-label');
    } else {
      el.setAttribute('aria-label', cfg.label);
      el.removeAttribute('aria-labelledby');
    }
    /* -1 rather than removal: keeps it programmatically focusable as the
       trap's fallback target, without adding a tab stop. */
    var ti = el.getAttribute('tabindex');
    if (ti === null || ti === '0') el.setAttribute('tabindex', '-1');
  }

  /* --- Nav-drawer-only corrections ---------------------------------------- */
  function navFixes(el) {
    var ham = d.querySelector('.header__mobile__hamburger');
    /* aria-controls pointed at "header-menu", which does not exist. */
    if (ham && el.id && ham.getAttribute('aria-controls') !== el.id) {
      ham.setAttribute('aria-controls', el.id);
    }
    var close = el.querySelector('.drawer__close');
    if (close) {
      if (close.getAttribute('aria-label') !== 'Close menu') {
        close.setAttribute('aria-label', 'Close menu');
      }
      /* It was stuck reading "true" after the drawer had closed. */
      close.setAttribute('aria-expanded', isOpen(el) ? 'true' : 'false');
    }
  }

  /* --- Focus trap ---------------------------------------------------------
     focusin, not a Tab keydown handler: it catches Tab, Shift+Tab and
     programmatic focus alike, which is what the audit proved was missing. */
  d.addEventListener('focusin', function (e) {
    var open = openOverlay();
    if (!open) { lastOutside = e.target; return; }
    if (open.contains(e.target)) return;
    var f = focusables(open);
    if (f.length) { f[0].focus(); }
    else { try { open.focus(); } catch (err) {} }
  }, true);

  /* --- Focus return ------------------------------------------------------- */
  function onStateChange(el, cfg) {
    if (!el) return;
    semantics(el, cfg);
    if (cfg.nav) navFixes(el);
    if (isOpen(el)) {
      el.__ftbWasOpen = true;
    } else if (el.__ftbWasOpen) {
      el.__ftbWasOpen = false;
      if (lastOutside && d.contains(lastOutside)) {
        try { lastOutside.focus(); } catch (err) {}
      }
    }
  }

  function boot() {
    OVERLAYS.forEach(function (cfg) {
      var el = d.querySelector(cfg.sel);
      if (!el) return;
      onStateChange(el, cfg);
      try {
        var o = new MutationObserver(function () { onStateChange(el, cfg); });
        o.observe(el, { attributes: true, attributeFilter: ['class'] });
      } catch (err) {}
    });
  }

  if (d.readyState !== 'loading') { boot(); }
  else { d.addEventListener('DOMContentLoaded', boot); }
})();
